// CTV-610: in-browser filmstrip thumbnails for the timeline editor. Seeks a
// clip's local object URL and grabs evenly-spaced frames onto a canvas — no
// server round-trip. filmstripSlots() (pure) then maps those frames onto the
// slots of a bar for the clip's current [inSec, inSec+lengthSec] window, so
// panning/resizing just re-picks from the already-extracted set.

export interface ClipThumb {
  t: number; // source time (seconds) the frame was grabbed at
  url: string; // data URL
}

const seekTo = (v: HTMLVideoElement, t: number) =>
  new Promise<void>((resolve) => {
    const onSeeked = () => {
      v.removeEventListener('seeked', onSeeked);
      resolve();
    };
    v.addEventListener('seeked', onSeeked);
    v.currentTime = t;
  });

/**
 * Grab ~one frame every couple seconds (6–16 total) across the whole source
 * clip. Best-effort: resolves to [] on any decode/seek failure.
 */
export async function extractThumbnails(srcUrl: string, thumbW = 96): Promise<ClipThumb[]> {
  try {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'auto';
    // Intake clips play from signed Supabase URLs on another origin; without
    // an anonymous CORS fetch the canvas taints and toDataURL throws, which
    // silently emptied the filmstrip (CTV-745). Harmless for blob: URLs.
    v.crossOrigin = 'anonymous';
    v.src = srcUrl;
    await new Promise<void>((resolve, reject) => {
      v.onloadeddata = () => resolve();
      v.onerror = () => reject(new Error('thumbnail: video load failed'));
    });
    const dur = v.duration || 0;
    if (!dur || !v.videoWidth) return [];

    const count = Math.max(6, Math.min(16, Math.round(dur / 2)));
    const thumbH = Math.max(1, Math.round(thumbW * (v.videoHeight / v.videoWidth)));
    const canvas = document.createElement('canvas');
    canvas.width = thumbW;
    canvas.height = thumbH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return [];

    const thumbs: ClipThumb[] = [];
    for (let i = 0; i < count; i++) {
      const t = Math.min(dur - 0.05, (dur * (i + 0.5)) / count);
      await seekTo(v, Math.max(0, t));
      ctx.drawImage(v, 0, 0, thumbW, thumbH);
      thumbs.push({ t, url: canvas.toDataURL('image/jpeg', 0.55) });
    }
    return thumbs;
  } catch {
    return [];
  }
}

/**
 * Lay ALL the clip's frames out as one strip covering the whole source, then
 * show only the current [inSec, inSec+lengthSec] window through the bar. Returns
 * the strip's width (as a % of the bar) and its horizontal shift (as a % of the
 * strip's own width). Because it's a continuous translate, panning the in-point
 * slides the frames smoothly rather than stepping between thumbnails.
 */
export function filmstripTransform(
  durationSec: number,
  inSec: number,
  lengthSec: number,
): { widthPct: number; translatePct: number } {
  if (durationSec <= 0 || lengthSec <= 0) return { widthPct: 100, translatePct: 0 };
  return {
    widthPct: (durationSec / lengthSec) * 100,
    translatePct: -(inSec / durationSec) * 100 || 0, // `|| 0` avoids -0
  };
}
